import { Component } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { interval, Subscription } from 'rxjs';
import { Transaccion } from '../../interfaces/transaccion.interface';
import { TransaccionService } from '../../services/transaccion.service';

import { Clipboard } from '@angular/cdk/clipboard';
import { CurrencyPipe } from '@angular/common';
import { Router } from '@angular/router';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { GetTransactionsType } from 'src/app/shared/enums/get-transactions-type';
import {
  ParameterTypeEnum,
  ParameterValueEnum,
} from 'src/app/shared/enums/parameter-type.enum';
import { ActionsService } from 'src/app/shared/services/actions.service';
import Swal from 'sweetalert2';
import { BinanceService } from '../../services/binance.service';
import { ParameterService } from '../../services/parameter.service';
import { AssignTransactionComponent } from '../admin-transactions/assign-transaction/assign-transaction.component';
import { EditTransactionModalComponent } from './edit-transaction-modal/edit-transaction-modal.component';
import { RejectTransactionModalComponent } from './reject-transaction-modal/reject-transaction-modal.component';
import { SendTransactionModalComponent } from './send-transaction-modal/send-transaction-modal.component';

@Component({
  selector: 'app-assigned-transactions',
  templateUrl: './assigned-transactions.component.html',
  styleUrls: ['./assigned-transactions.component.sass'],
  animations: [
    // Slide ascendente continuo para el toast flotante.
    // :enter → entra desde abajo subiendo hasta su posición final.
    // :leave → continúa subiendo y se desvanece por arriba.
    trigger('slideToast', [
      transition(':enter', [
        style({ transform: 'translateY(40px)', opacity: 0 }),
        animate('320ms cubic-bezier(0.22, 1, 0.36, 1)', style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ transform: 'translateY(0)', opacity: 1 }),
        animate('280ms cubic-bezier(0.4, 0, 1, 1)', style({ transform: 'translateY(-40px)', opacity: 0 })),
      ]),
    ]),
  ],
})
export class AssignedTransactionsComponent {
  transacciones: Transaccion[] = [];
  seleccionados: number = 0;
  dataSource: MatTableDataSource<any> | undefined;
  subscription: Subscription = new Subscription();
  expandedCardId: number | null = null;
  displayedColumns: string[] = [
    'seleccion',
    'id',
    'ruta',
    'idUser',
    'userId',
    'cuentaBeneficiarioId',
    'tipoCuentaBeneficiarioId',
    'cuentaTrabajo',
    'beneficiarioDNI',
    'bancoDestino',
    'beneficiarioTipoCuenta',
    'numeroCuenta',
    'totalMonedaDestino',
    'precioVenta',
    'pago',
    'acciones',
  ];
  emailOperador = '';
  cambios: any = {};
  isBinance: boolean = false;
  currentRates: any;
  currentValues: any;

  /**
   * Modo de cálculo de la tasa FIAT que ve el operador. Se lee desde el
   * parámetro `TIPO_DE_TASA_OPERADOR_WALLET`. Si su valor es `'MANUAL'`
   * `currentRate()` devuelve `manualRate` (parámetro `VALOR_RATE_MANUAL`);
   * cualquier otro valor cae al promedio de los 4 bancos VES (Binance).
   */
  fiatMode: string = '';
  manualRate: number = 0;

  /**
   * Intervalo de auto-refresh de tasas + modo FIAT mientras el operador
   * tiene la vista abierta. Mantiene `currentRate()` al día sin obligar
   * a refrescar manualmente. La suscripción vive dentro de
   * `this.subscription` → se cancela en `ngOnDestroy`.
   */
  private readonly RATES_REFRESH_INTERVAL_MS = 60_000;

  /**
   * Refresco automático del listado. Cuando el rail automático (N58 /
   * Banco Plaza) resuelve una tx, ésta cambia de estado y deja de venir en
   * la query ASSIGNED (que sólo trae POR_PROCESAR) → así la fila bloqueada
   * desaparece sola sin que el operador refresque a mano. Se saltea si hay
   * selección sin guardar para no pisar el trabajo en curso.
   */
  private readonly TRANSACTIONS_REFRESH_INTERVAL_MS = 30_000;

  /**
   * Cuentas de trabajo dedicadas a los rails automáticos, leídas de los
   * parámetros CUENTA_TRABAJO_N58 / CUENTA_TRABAJO_BANCO_PLAZA (el `valor`
   * es el `cuenta_trabajo_id`). Una tx asignada a una de estas cuentas y aún
   * POR_PROCESAR está siendo pagada (o en verificación) por ese rail → se
   * bloquea en la vista para que ningún operador la tome en paralelo. Es un
   * candado SÓLO visual; la protección dura vive en el backend.
   */
  private railWorkingAccounts = new Map<number, 'N58' | 'Banco Plaza'>();

  /**
   * Modo display normalizado mostrado en el banner. Se deriva de
   * `fiatMode` (raw del parámetro `TIPO_DE_TASA_OPERADOR_WALLET`) y
   * siempre cae en uno de tres estados visibles para el operador.
   */
  fiatRateModeDisplay: 'MANUAL' | 'CBPAY' | 'BINANCE' = 'BINANCE';

  /**
   * Notificación temporal que aparece sólo cuando el polling detecta un
   * cambio real de modo entre dos cargas. Persiste ~6s y se limpia
   * sola; `null` cuando no hay cambio activo.
   */
  rateModeChange: { from: 'MANUAL' | 'CBPAY' | 'BINANCE'; to: 'MANUAL' | 'CBPAY' | 'BINANCE' } | null = null;

  /**
   * Flag para saber si ya hubo una carga inicial exitosa. Evita
   * disparar la notificación de "cambio" en la primera lectura
   * (cuando comparar contra el default sería ruido).
   */
  private fiatModeInitialized: boolean = false;

  /** Timeout activo de la notificación de cambio (para poder limpiarlo). */
  private rateModeChangeTimeout: any = null;

  private readonly RATE_MODE_NOTIFICATION_MS = 6_000;

  constructor(
    private transactionService: TransaccionService,
    private dialog: MatDialog,
    private auth: CognitoService,
    private actionService: ActionsService,
    private router: Router,
    private clipboard: Clipboard,
    private currencyPipe: CurrencyPipe,
    private binanceService: BinanceService,
    private parameterService: ParameterService,
  ) {
    this.isBinance = this.router.url.includes('pending-transactions-binance');
    this.getCurrentRates();
    this.getCurrentValues();
    this.loadFiatRateMode();
  }

  /**
   * Carga los parámetros `TIPO_DE_TASA_OPERADOR_WALLET` y
   * `VALOR_RATE_MANUAL` y los cachea en `fiatMode` / `manualRate`. Si la
   * lectura falla queda en default (`fiatMode = ''`, `manualRate = 0`)
   * que dispara el fallback al promedio calculado.
   */
  private async loadFiatRateMode(): Promise<void> {
    try {
      const [modeParam, manualParam] = await Promise.all([
        this.parameterService.getParameter(ParameterTypeEnum.TIPO_DE_TASA_OPERADOR_WALLET),
        this.parameterService.getParameter(ParameterTypeEnum.VALOR_RATE_MANUAL),
      ]);
      const newRawMode = (modeParam?.valor ?? '').toString().toUpperCase();
      const prevDisplay = this.fiatRateModeDisplay;
      const newDisplay = this.mapFiatModeToDisplay(newRawMode);

      // Sólo notificamos cuando el polling detecta un cambio real:
      // necesita haber al menos una carga inicial completada Y que el
      // modo display efectivamente difiera del anterior.
      if (this.fiatModeInitialized && newDisplay !== prevDisplay) {
        this.triggerRateModeChangeNotification(prevDisplay, newDisplay);
      }

      this.fiatMode = newRawMode;
      this.fiatRateModeDisplay = newDisplay;
      const rawRate = Number(manualParam?.valor);
      this.manualRate = Number.isFinite(rawRate) ? +rawRate : 0;
      this.fiatModeInitialized = true;
    } catch {
      this.fiatMode = '';
      this.manualRate = 0;
      this.fiatRateModeDisplay = 'BINANCE';
    }
  }

  /**
   * Mapea el valor raw del parámetro `TIPO_DE_TASA_OPERADOR_WALLET` al
   * modo display que ve el operador. La rama default cae en `BINANCE`
   * porque `currentRate()` usa el promedio de los 4 bancos venezolanos
   * cuando no es `MANUAL` ni `CBPAY` — esos datos vienen de Binance.
   */
  private mapFiatModeToDisplay(raw: string): 'MANUAL' | 'CBPAY' | 'BINANCE' {
    const normalized = (raw || '').toUpperCase();
    if (normalized === 'MANUAL') return 'MANUAL';
    if (normalized === 'CBPAY') return 'CBPAY';
    return 'BINANCE';
  }

  /**
   * Activa el toast temporal "Tasa cambió de X a Y". Si ya había una
   * notificación activa, reinicia el timer para que el cambio más
   * reciente se vea durante el tiempo completo.
   */
  private triggerRateModeChangeNotification(
    from: 'MANUAL' | 'CBPAY' | 'BINANCE',
    to: 'MANUAL' | 'CBPAY' | 'BINANCE',
  ): void {
    this.rateModeChange = { from, to };
    if (this.rateModeChangeTimeout) {
      clearTimeout(this.rateModeChangeTimeout);
    }
    this.rateModeChangeTimeout = setTimeout(() => {
      this.rateModeChange = null;
      this.rateModeChangeTimeout = null;
    }, this.RATE_MODE_NOTIFICATION_MS);
  }

  dismissRateModeChange(): void {
    this.rateModeChange = null;
    if (this.rateModeChangeTimeout) {
      clearTimeout(this.rateModeChangeTimeout);
      this.rateModeChangeTimeout = null;
    }
  }

  async refreshRates() {
    await Promise.all([
      this.getCurrentRates(),
      this.loadFiatRateMode(),
    ]);
  }

  /**
   * Tasa actual FIAT mostrada al operador.
   *
   * Comportamiento según el parámetro `TIPO_DE_TASA_OPERADOR_WALLET`:
   *  - `MANUAL` → devuelve `VALOR_RATE_MANUAL` cacheado en `manualRate`.
   *  - cualquier otro valor (`BINANCE`, `CBPAY`, vacío, error de carga)
   *    → promedia los 4 bancos VES (Provincial, Banesco, Mercantil,
   *    Venezuela) — fallback histórico de Binance.
   *
   * Como `loadFiatRateMode()` es async y `currentRate()` se llama desde
   * el template, los valores arrancan en defaults seguros (`fiatMode =
   * ''`, `manualRate = 0`) → cae al promedio hasta que termina la carga.
   */
  currentRate() {
    if (this.fiatMode === ParameterValueEnum.MANUAL) {
      return this.manualRate;
    }
    if (!this.currentRates?.valoresVES) return 0;
    return (
      (+this.currentRates.valoresVES.valorProvincial +
        +this.currentRates.valoresVES.valorBanesco +
        +this.currentRates.valoresVES.valorMercantil +
        +this.currentRates.valoresVES.valorVenezuela) /
      4
    );
  }

  async ngOnInit() {
    await this.loadRailWorkingAccounts();
    await this.loadTransactions();
    this.subscription.add(
      this.actionService.subjectAssignedTransaction.subscribe(async (res) => {
        if (res) {
          this.loadTransactions();
        }
      }),
    );

    // Auto-refresh tasas + modo FIAT cada minuto mientras la vista está abierta.
    this.subscription.add(
      interval(this.RATES_REFRESH_INTERVAL_MS).subscribe(() => {
        this.refreshRates();
      }),
    );

    // Auto-refresh del listado: las tx que el rail automático (N58 / Banco
    // Plaza) ya resolvió cambian de estado y dejan de venir en ASSIGNED →
    // así las filas bloqueadas desaparecen solas. Se saltea si el operador
    // tiene una selección sin guardar (cambios) para no perder su trabajo.
    this.subscription.add(
      interval(this.TRANSACTIONS_REFRESH_INTERVAL_MS).subscribe(() => {
        if (this.isObjectEmpty(this.cambios)) {
          this.loadRailWorkingAccounts();
          this.loadTransactions();
        }
      }),
    );
  }

  async loadTransactions() {
    let authData = await this.auth.getUser();
    this.emailOperador = authData.attributes.email;
    const bankType = this.isBinance ? 'BINANCE' : undefined;
    const transaccionesResult =
      await this.transactionService.getAllTransactions(
        GetTransactionsType.ASSIGNED,
        bankType,
      );
    this.transacciones = Array.isArray(transaccionesResult)
      ? transaccionesResult
      : ((transaccionesResult as any)?.query ?? []);
    this.dataSource = new MatTableDataSource(this.transacciones);
    this.cambios = {};
  }

  /**
   * Lee CUENTA_TRABAJO_N58 y CUENTA_TRABAJO_BANCO_PLAZA y arma el mapa
   * `railWorkingAccounts` (cuenta_trabajo_id → rail). Si la lectura falla el
   * mapa queda vacío → no se bloquea nada (comportamiento histórico), nunca
   * al revés: preferimos no bloquear de más ante un error de parámetros.
   */
  private async loadRailWorkingAccounts(): Promise<void> {
    try {
      const [n58Param, bpParam] = await Promise.all([
        this.parameterService.getParameter(ParameterTypeEnum.CUENTA_TRABAJO_N58),
        this.parameterService.getParameter(
          ParameterTypeEnum.CUENTA_TRABAJO_BANCO_PLAZA,
        ),
      ]);
      const map = new Map<number, 'N58' | 'Banco Plaza'>();
      const n58Id = Number(n58Param?.valor);
      const bpId = Number(bpParam?.valor);
      if (Number.isFinite(n58Id) && n58Id > 0) map.set(n58Id, 'N58');
      if (Number.isFinite(bpId) && bpId > 0) map.set(bpId, 'Banco Plaza');
      this.railWorkingAccounts = map;
    } catch {
      this.railWorkingAccounts = new Map();
    }
  }

  /**
   * Rail automático que está procesando la fila, o null. Una tx cuya cuenta
   * de trabajo es la de N58 o Banco Plaza sigue POR_PROCESAR mientras el rail
   * paga (o queda en verificación), pero NO debe poder tomarla un operador.
   * Como la query ASSIGNED sólo trae POR_PROCESAR, en cuanto el rail resuelve
   * (ENVIADA/RECHAZADA) la fila sale del listado y el candado se va solo.
   */
  procesandoRail(row: any): 'N58' | 'Banco Plaza' | null {
    const id = Number(row?.cuentaTrabajoID ?? row?.cuentaTrabajo?.cuenta_trabajo_id);
    if (!Number.isFinite(id) || id <= 0) return null;
    return this.railWorkingAccounts.get(id) ?? null;
  }

  /** Helper booleano para el template. */
  isProcesandoRail(row: any): boolean {
    return this.procesandoRail(row) !== null;
  }

  async getCurrentRates() {
    this.currentRates = await this.binanceService.getCurrentRates();
  }
  async getCurrentValues() {
    this.currentValues = await this.binanceService.getConversionData();
  }

  isObjectEmpty = (objectName: any) => {
    return Object.keys(objectName).length === 0;
  };
  copy(element: Transaccion) {
    const text = `${element.cuentaBeneficiario.nombre} ${element.cuentaBeneficiario.apellidos}\n${element.cuentaBeneficiario.numeroID}\n${element.cuentaBeneficiario.numeroCuenta}\n${element.cuentaBeneficiario.banco?.nombre}`;
    this.clipboard.copy(text);
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });

    Toast.fire({
      icon: 'success',
      title: 'Transferencia copiada al portapapeles',
    });
  }
  async setPurchaseValue(transactionId: number, id: number) {
    if (!transactionId || !id) return;

    const value = (<HTMLInputElement>(
      document.getElementById(`valorCompra${id}`)
    )).value.replaceAll(',', '');
    Swal.fire({
      title: 'Guardando precio de compra ',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    await this.transactionService.setPurchaseValue({
      transactionId,
      value,
    });
    Swal.close();
  }
  async guardarSeleccion() {
    const cambioFormateados = Object.keys(this.cambios).map((key) => {
      return {
        id: +key,
        checked: this.cambios[key],
      };
    });
    const body = {
      idTransaction: cambioFormateados,
      operatorEmail: this.emailOperador,
    };
    await this.transactionService.changeAssignationStatus(body);
    await this.loadTransactions();
  }
  change(event: any, id: number) {
    this.cambios[id] = event.checked;
  }

  checkEmailOperador(email: string) {
    if (!email) return false;
    if (this.emailOperador !== email) return true;
    return false;
  }

  disableButton(email: string) {
    if (this.emailOperador !== email) return true;
    return false;
  }

  limpiarSeleccion() {
    const data =
      this.dataSource?.data.map((value) => {
        return { ...value, seleccionado: false };
      }) || [];

    if (this.dataSource && this.dataSource.data) {
      this.dataSource.data = data;
    }
    // this.change();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.rateModeChangeTimeout) {
      clearTimeout(this.rateModeChangeTimeout);
      this.rateModeChangeTimeout = null;
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    if (this.dataSource) {
      this.dataSource.filter = filterValue.trim().toUpperCase();
    }
  }

  public doFilter = (event: any) => {
    const element = event.currentTarget as HTMLInputElement;
    const value = element.value;
    if (this.dataSource)
      this.dataSource.filter = value.trim().toLocaleLowerCase();
  };
  //TPDP

  returnSelectedTransactions(row: Transaccion) {
    const selected =
      this.dataSource?.data?.filter((x) => x.seleccionado && x.id !== row.id) ||
      [];
    selected.push(row);
    return selected;
  }

  formatCurrencyForCopy(value: number): string {
    const valueString = value.toString();
    return valueString.replace(/\./g, ',');
  }

  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  openSendTransactionModal(row: any) {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(SendTransactionModalComponent, {
      data: row,
      maxHeight: '90vh',
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '800px',
      width: isMobile ? '95vw' : undefined,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        this.loadTransactions();
      }
    });
  }

  openEditTransactionModal(row: any) {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(EditTransactionModalComponent, {
      data: row,
      maxHeight: '90vh',
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '800px',
      width: isMobile ? '95vw' : undefined,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        this.loadTransactions();
      }
    });
  }

  openRejectTransactionModal(row: any) {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(RejectTransactionModalComponent, {
      data: row,
      maxHeight: '90vh',
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '800px',
      width: isMobile ? '95vw' : undefined,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        this.loadTransactions();
      }
    });
  }

  async reasign(row: any) {
    row.isEditing = false;
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(AssignTransactionComponent, {
      data: {
        trxId: row.id,
        type: 'reassign',
      },
      maxHeight: '90vh',
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '800px',
      width: isMobile ? '95vw' : undefined,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        this.loadTransactions();
      }
    });
  }
}
